import React, { useMemo, useRef, useContext } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLiveAPIUniversal } from "../../hooks/use-live-api-universal";
import { useAuth } from "@clerk/clerk-react";
import { SettingsContext } from "../../contexts/SettingsContext";
import { ADHDAPIAdapter } from "../../lib/adhd-api-adapter";
import { useAPIClient } from "../../lib/api-client";
import { CollapsibleProjectTreeRef } from "./CollapsibleProjectTree";
import { useADHDData } from "./hooks/useADHDData";
import { useOverwhelm } from "./hooks/useOverwhelm";
import { useToolCalls } from "./hooks/useToolCalls";
import { FunctionCallProcessor } from "./services/FunctionCallProcessor";
import { ADHDAssistantContent } from "./components/ADHDAssistantContent";

export interface ADHDAssistantProps {
  videoRef?: React.RefObject<HTMLVideoElement>;
  videoStream?: MediaStream | null;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
  onNavigateToSettings?: () => void;
}

const ADHDAssistantContainer = React.memo<ADHDAssistantProps>(
  ({ videoRef, videoStream, onVideoStreamChange, onNavigateToSettings }) => {
    const { client } = useLiveAPIUniversal();
    const { isSignedIn } = useAuth();
    const apiClient = useAPIClient();

    // Safe settings access with fallback
    const settingsContext = useContext(SettingsContext);
    const userTimezone = useMemo(() => {
      return (
        settingsContext?.settings?.user?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    }, [settingsContext?.settings?.user?.timezone]);

    // Refs
    const projectTreeRef = useRef<CollapsibleProjectTreeRef>(null);

    // ADHD Adapter
    const adhdAdapter = useMemo(() => {
      const adapter = new ADHDAPIAdapter();
      if (isSignedIn) {
        adapter.setAPIClient(apiClient);
      }
      return adapter;
    }, [isSignedIn, apiClient]);

    // Custom hooks for state management
    const adhdData = useADHDData();

    // Function call processor
    const functionCallProcessor = useMemo(() => {
      if (!adhdAdapter) return null;

      return new FunctionCallProcessor(adhdAdapter, projectTreeRef, {
        collapseAllProjects: () => projectTreeRef.current?.collapseAllProjects(),
        checkForOverwhelm: () => {}, // Will be set by overwhelm hook
        setAiPersonalityMode: adhdData.setAiPersonalityMode,
      });
    }, [adhdAdapter, adhdData.setAiPersonalityMode]);

    // Overwhelm detection and management
    const overwhelmData = useOverwhelm(
      adhdData.todosRef,
      adhdData.projectsRef,
      adhdData.currentEnergyLevel,
      functionCallProcessor,
      adhdData.setAiPersonalityMode
    );

    // Update the function call processor with the overwhelm check function
    if (functionCallProcessor) {
      (functionCallProcessor as any).actions.checkForOverwhelm = overwhelmData.checkForOverwhelm;
    }

    // Tool calls handling
    useToolCalls({
      client,
      adhdAdapter,
      userTimezone,
      addTodoOptimistically: adhdData.addTodoOptimistically,
      removeTodoOptimistically: adhdData.removeTodoOptimistically,
      updateTodosOptimistically: adhdData.updateTodosOptimistically,
      addProjectOptimistically: adhdData.addProjectOptimistically,
      updateProjectOptimistically: adhdData.updateProjectOptimistically,
      removeProjectOptimistically: adhdData.removeProjectOptimistically,
      setBodyDoublingSession: session => {
        // This would need to be handled by the state hook
      },
      setTodos: setter => {
        // This would need to be handled by the state hook
      },
      setProjects: setter => {
        // This would need to be handled by the state hook
      },
      functionCallProcessor,
      overwhelmAssessment: overwhelmData.overwhelmAssessment,
      aiPersonalityMode: adhdData.aiPersonalityMode,
    });

    return (
      <DndProvider backend={HTML5Backend}>
        <ADHDAssistantContent
          // Data props
          todos={adhdData.todos}
          projects={adhdData.projects}
          bodyDoublingSession={adhdData.bodyDoublingSession}
          isLoading={adhdData.isLoading}
          showCreateModal={adhdData.showCreateModal}
          editingTodo={adhdData.editingTodo}
          overwhelmAssessment={overwhelmData.overwhelmAssessment}
          currentEnergyLevel={adhdData.currentEnergyLevel}
          aiPersonalityMode={adhdData.aiPersonalityMode}
          // Action props
          setShowCreateModal={adhdData.setShowCreateModal}
          setEditingTodo={adhdData.setEditingTodo}
          startBodyDoubling={adhdData.startBodyDoubling}
          checkForOverwhelm={overwhelmData.checkForOverwhelm}
          // Refs
          projectTreeRef={projectTreeRef}
          loadingRef={adhdData.loadingRef}
          // Other props
          videoRef={videoRef}
          videoStream={videoStream}
          onVideoStreamChange={onVideoStreamChange}
          onNavigateToSettings={onNavigateToSettings}
          // Services
          adhdAdapter={adhdAdapter}
          userTimezone={userTimezone}
        />
      </DndProvider>
    );
  }
);

ADHDAssistantContainer.displayName = "ADHDAssistantContainer";

export default ADHDAssistantContainer;
